import React from 'react'
import { Row, Col } from 'antd'
import classNames from 'classnames'

import Button, { ButtonProps } from 'components/Button'
import FormattedMessage from 'components/FormattedMessage'
import Layer, { LayerProps } from 'components/Layer'
import { IntlMessageKeys } from 'components/FormattedMessage/types'

import './index.less'

const Confirmation: React.FC<ConfirmationProps> = (props) => {
  const {
    image,
    intro,
    title,
    children,
    maxWidth = 475,
    submitting = false,
    closeHandler,
    cancelHandler = closeHandler,
    submitHandler,
    closeButtonType = 'danger',
    submitButtonType = 'primary-bordered',
    cancelButtonOptions,
    submitButtonOptions,
    closeTranslationKey,
    submitTranslationKey,
    submitHintTranslationKey,
    disableSubmitButton,
    expandSubmitButton,
    stackedButtons,
    className,
    isLoading,
    ...layerProps
  } = props

  const showCancelButton = !!(closeTranslationKey && cancelHandler)
  let submitButtonOffset = 0
  const buttonWidths = {
    close: stackedButtons ? 24 : expandSubmitButton ? 10 : 12,
    submit: stackedButtons ? 24 : expandSubmitButton ? 14 : 12,
  }

  if (!showCancelButton && !stackedButtons) {
    submitButtonOffset = expandSubmitButton ? 5 : 6
  }

  const closeCol = showCancelButton && (
    <Col span={buttonWidths.close} key="close">
      <Button
        {...cancelButtonOptions}
        type={closeButtonType}
        size="large"
        disabled={submitting}
        className="ant-btn--with-border-radius-large ant-btn--brand-font"
        onClick={cancelHandler}
        translationKey={closeTranslationKey}
      />
    </Col>
  )

  const submitCol = (
    <Col span={buttonWidths.submit} offset={submitButtonOffset} key="submit">
      <Button
        {...submitButtonOptions}
        type={submitButtonType}
        size="large"
        loading={submitting}
        disabled={disableSubmitButton}
        className="ant-btn--with-border-radius-large ant-btn--brand-font"
        onClick={submitHandler}
        translationKey={submitTranslationKey}
      />

      {submitHintTranslationKey && (
        <div className="confirmation-overlay__buttons__submit-hint">
          <FormattedMessage id={submitHintTranslationKey} />
        </div>
      )}
    </Col>
  )

  return (
    <Layer
      maxWidth={maxWidth}
      className={classNames('confirmation-overlay', className)}
      onClose={closeHandler}
      isLoading={isLoading}
      {...layerProps}
      footer={!isLoading && (
        <Row gutter={[12, 6]} className="confirmation-overlay__buttons">
          {stackedButtons ? [submitCol, closeCol] : [closeCol, submitCol] }
        </Row>
      )}
    >
      <div className="confirmation-overlay__body">
        {image && (
          <div className="confirmation-overlay__body__image">
            {image}
          </div>
        )}

        {title && (
          <h4 className="confirmation-overlay__body__title">
            {title}
          </h4>
        )}

        {intro && (
          <p className="confirmation-overlay__body__intro">
            {intro}
          </p>
        )}

        {children && (
          <div className="confirmation-overlay__body__content">
            {children}
          </div>
        )}
      </div>
    </Layer>
  )
}

export type ConfirmationProps = Omit<LayerProps, 'onClose'> & {
  className?: string
  closeHandler?: () => void
  closeTranslationKey?: IntlMessageKeys
  cancelHandler?: () => void
  submitHandler?: () => void
  submitTranslationKey?: IntlMessageKeys
  cancelButtonOptions?: ButtonProps
  submitButtonOptions?: ButtonProps
  submitHintTranslationKey?: IntlMessageKeys
  stackedButtons?: boolean
  image?: React.ReactNode
  title?: React.ReactNode
  intro?: React.ReactNode
  submitting?: boolean
  disableSubmitButton?: boolean
  submitButtonType?: ButtonProps['type']
  expandSubmitButton?: boolean
  closeButtonType?: ButtonProps['type']
}

export default Confirmation
